'use client';

import React from "react";
import { 
  Bot, Zap, Wand2, Paintbrush, Wrench, LucideIcon,
  BarChart2, Settings, MessageCircle, Code2, FileText,
  Database, GraduationCap, DollarSign, Gamepad2, HeartPulse,
  Image, Languages, Megaphone, Music, CheckCircle,
  Search, Shield, Users, Mic, Video
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface TagBadgeProps {
  tag: string;
  icon?: LucideIcon;
  className?: string;
}

const defaultIcons: Record<string, LucideIcon> = {
  ai: Bot,
  ml: Bot,
  design: Paintbrush,
  saas: Zap,
  language: Languages,
  chat: MessageCircle,
  tools: Wrench,
  developer: Wrench,
  analytics: BarChart2,
  automation: Settings,
  code: Code2,
  content: FileText,
  data: Database,
  education: GraduationCap,
  finance: DollarSign,
  gaming: Gamepad2,
  health: HeartPulse,
  image: Image,
  marketing: Megaphone,
  music: Music,
  productivity: CheckCircle,
  research: Search,
  security: Shield,
  social: Users,
  speech: Mic,
  video: Video,
};

const tagColors: Record<string, string> = {
  ai: 'bg-blue-100 text-blue-800',
  ml: 'bg-blue-100 text-blue-800',
  design: 'bg-purple-100 text-purple-800',
  saas: 'bg-green-100 text-green-800',
  language: 'bg-yellow-100 text-yellow-800',
  chat: 'bg-pink-100 text-pink-800',
  tools: 'bg-indigo-100 text-indigo-800',
  developer: 'bg-teal-100 text-teal-800',
  analytics: 'bg-cyan-100 text-cyan-800',
  automation: 'bg-orange-100 text-orange-800',
  code: 'bg-violet-100 text-violet-800',
  content: 'bg-amber-100 text-amber-800',
  data: 'bg-sky-100 text-sky-800',
  education: 'bg-emerald-100 text-emerald-800',
  finance: 'bg-lime-100 text-lime-800',
  gaming: 'bg-fuchsia-100 text-fuchsia-800',
  health: 'bg-rose-100 text-rose-800',
  image: 'bg-lightBlue-100 text-lightBlue-800',
  marketing: 'bg-orange-100 text-orange-800',
  music: 'bg-purple-100 text-purple-800',
  productivity: 'bg-teal-100 text-teal-800',
  research: 'bg-blue-100 text-blue-800',
  security: 'bg-red-100 text-red-800',
  social: 'bg-pink-100 text-pink-800',
  speech: 'bg-purple-100 text-purple-800',
  video: 'bg-indigo-100 text-indigo-800',
};

export function TagBadge({ tag, icon, className }: TagBadgeProps) {
  console.log('Rendering TagBadge with:', { tag, icon, className });
  
  // Normalize tag name and match partial keywords
  const normalizedTag = tag.toLowerCase();
  const matchedKey = Object.keys(defaultIcons).find(key => 
    normalizedTag.includes(key)
  ) || 'default';

  const Icon = icon || defaultIcons[matchedKey] || Wand2;
  const colorClass = tagColors[matchedKey] || 'bg-gray-100 text-gray-800';

  console.log('Using icon:', Icon, 'with color:', colorClass);

  return (
    <div
      className={cn(
        "rounded-full border text-xs font-semibold transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "border-transparent hover:bg-opacity-80",
        "flex items-center gap-1 px-2 py-1",
        colorClass,
        className
      )}
    >
      <Icon className="w-3 h-3" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {tag}
    </div>
  );
}
